import { useEffect, useCallback } from 'react';
import { socketService } from '@services/SocketService';
import { useGameStore, useRoomStore, useChatStore } from '@store/index';

// Track if listeners have been set up
let listenersSetUp = false;

export const useSocket = () => {
  const setGameState = useGameStore((state) => state.setGameState);
  // const setRoom = useRoomStore((state) => state.setRoom); // Not used
  const setOpponent = useRoomStore((state) => state.setOpponent);
  const addMessage = useChatStore((state) => state.addMessage);
  
  useEffect(() => {
    // Socket connection is managed by SocketProvider
    
    // Only set up listeners once
    if (listenersSetUp) {
      console.log('[useSocket] Listeners already set up, skipping...');
      return;
    }
    
    // Set up event listeners
    const handleGameStarted = (data: any) => {
      console.log('[useSocket] Game started event received:', data);
      console.log('[useSocket] Converting board data...');
      
      // Convert board squares object to Map
      if (data.gameState && data.gameState.board && data.gameState.board.squares) {
        const boardMap = new Map(Object.entries(data.gameState.board.squares));
        data.gameState.board.squares = boardMap;
        console.log('[useSocket] Board converted to Map with', boardMap.size, 'pieces');
      }
      
      console.log('[useSocket] Calling setGameState...');
      setGameState(data.gameState);
    };
    
    const handleMoveMade = (data: any) => {
      console.log('[useSocket] Move made event received:', data);
      console.log('[useSocket] Current game state before update:', useGameStore.getState().gameState);
      
      // Convert board squares object to Map
      if (data.gameState && data.gameState.board && data.gameState.board.squares) {
        const boardMap = new Map(Object.entries(data.gameState.board.squares));
        data.gameState.board.squares = boardMap;
        console.log('[useSocket] Board converted to Map with', boardMap.size, 'pieces');
      }
      
      console.log('[useSocket] Updating game state...');
      setGameState(data.gameState);
      console.log('[useSocket] Game state updated. New state:', data.gameState);
      
      // Add move to chat as system message if we have move notation
      if (data.gameState && data.gameState.moveHistory && data.gameState.moveHistory.length > 0) {
        const lastMove = data.gameState.moveHistory[data.gameState.moveHistory.length - 1];
        if (lastMove && lastMove.notation) {
          addMessage({
            id: Date.now().toString(),
            playerId: 'system',
            playerName: 'System',
            message: lastMove.notation.san,
            timestamp: Date.now(),
            type: 'move',
          });
        }
      }
    };
    
    const handlePlayerJoined = (data: any) => {
      console.log('Player joined event received:', data);
      setOpponent(data.player);
      
      addMessage({
        id: Date.now().toString(),
        playerId: 'system',
        playerName: 'System',
        message: `${data.player.name} joined the room`,
        timestamp: Date.now(),
        type: 'system',
      });
    };
    
    const handlePlayerLeft = (data: any) => {
      setOpponent(null);
      
      addMessage({
        id: Date.now().toString(),
        playerId: 'system',
        playerName: 'System',
        message: `${data.playerName} left the room`,
        timestamp: Date.now(),
        type: 'system',
      });
    };
    
    const handleChatMessage = (data: any) => {
      addMessage({
        id: Date.now().toString(),
        playerId: data.playerId,
        playerName: data.playerName,
        message: data.message,
        timestamp: data.timestamp,
        type: 'chat',
      });
    };
    
    const handleTimerUpdate = (data: any) => {
      const currentGameState = useGameStore.getState().gameState;
      if (currentGameState) {
        // Update only the timer data to prevent overwriting other state changes
        const updatedState = {
          ...currentGameState,
          timers: {
            white: {
              ...currentGameState.timers.white,
              remaining: data.white
            },
            black: {
              ...currentGameState.timers.black,
              remaining: data.black
            }
          }
        };
        setGameState(updatedState);
      }
    };
    
    const handleGameEnded = (data: any) => {
      console.log('[useSocket] Game ended event received:', data);
      
      if (data.gameState) {
        // Convert board squares object to Map if needed
        if (data.gameState.board && data.gameState.board.squares) {
          const boardMap = new Map(Object.entries(data.gameState.board.squares));
          data.gameState.board.squares = boardMap;
        }
        
        setGameState(data.gameState);
        
        // Add game end message to chat
        let endMessage = '';
        if (data.reason === 'timeout') {
          endMessage = `Game ended - ${data.winner} wins by timeout!`;
        } else if (data.reason === 'checkmate') {
          endMessage = `Game ended - ${data.winner} wins by checkmate!`;
        } else if (data.reason === 'stalemate') {
          endMessage = 'Game ended - Draw by stalemate!';
        } else if (data.reason === 'resignation') {
          endMessage = `Game ended - ${data.winner} wins by resignation!`;
        } else if (data.reason === 'draw-agreement') {
          endMessage = 'Game ended - Draw by agreement!';
        } else {
          endMessage = `Game ended - ${data.reason}`;
        }
        
        addMessage({
          id: Date.now().toString(),
          playerId: 'system',
          playerName: 'System',
          message: endMessage,
          timestamp: Date.now(),
          type: 'system',
        });
      }
    };

    const handleDrawOffered = (data: any) => {
      console.log('[useSocket] Draw offered event received:', data);
      
      addMessage({
        id: Date.now().toString(),
        playerId: 'system',
        playerName: 'System',
        message: `${data.playerName} offers a draw. Click "Accept Draw" to accept or "Decline Draw" to decline.`,
        timestamp: Date.now(),
        type: 'system',
      });
    };

    const handleDrawDeclined = (data: any) => {
      console.log('[useSocket] Draw declined event received:', data);
      
      addMessage({
        id: Date.now().toString(),
        playerId: 'system',
        playerName: 'System',
        message: 'Your draw offer was declined.',
        timestamp: Date.now(),
        type: 'system',
      });
    };
    
    const handleError = (data: any) => {
      console.error('[useSocket] Error event received:', data);
      
      addMessage({
        id: Date.now().toString(),
        playerId: 'system',
        playerName: 'System',
        message: `Error: ${data?.error || 'Unknown error occurred'}`,
        timestamp: Date.now(),
        type: 'system',
      });
    };
    
    // Register listeners
    console.log('[useSocket] Registering event listeners...');
    socketService.on('gameStarted', handleGameStarted);
    socketService.on('moveMade', handleMoveMade);
    socketService.on('playerJoined', handlePlayerJoined);
    socketService.on('playerLeft', handlePlayerLeft);
    socketService.on('chatMessage', handleChatMessage);
    socketService.on('timerUpdate', handleTimerUpdate);
    socketService.on('gameEnded', handleGameEnded);
    socketService.on('drawOffered', handleDrawOffered);
    socketService.on('drawDeclined', handleDrawDeclined);
    socketService.on('error', handleError);
    console.log('[useSocket] Event listeners registered');
    
    // Mark listeners as set up
    listenersSetUp = true;
    
    // Don't clean up listeners on unmount - they should stay active
    return () => {
      // Keep listeners active across component unmounts
      console.log('[useSocket] Component unmounting but keeping listeners active');
    };
  }, []); // Empty deps array - only run once
  
  const createRoom = useCallback(async (playerName: string, settings: any) => {
    return socketService.createRoom(playerName, settings);
  }, []);
  
  const joinRoom = useCallback(async (roomCode: string, playerName: string) => {
    return socketService.joinRoom(roomCode, playerName);
  }, []);
  
  const makeMove = useCallback((move: any) => {
    socketService.makeMove(move);
  }, []);
  
  const sendMessage = useCallback((message: string) => {
    socketService.sendChatMessage(message);
  }, []);
  
  return {
    isConnected: socketService.isConnected(),
    createRoom,
    joinRoom,
    makeMove,
    sendMessage,
    resign: () => socketService.resign(),
    offerDraw: () => socketService.offerDraw(),
    acceptDraw: () => socketService.acceptDraw(),
    declineDraw: () => socketService.declineDraw(),
  };
};